<template>
  <div>
    <small v-if="editLink"><a :href="editLink">Edit Standard</a></small>

    <blockquote v-if="corePurpose">
      <strong>Core Purpose</strong>
      <p v-html="corePurpose"/>
    </blockquote>

    <template v-if="setCode === 'DLM-EE'">
      <h3>{{ infoObj.cc_cluster }}</h3>
      <div v-html="standardText"/>
    </template>

    <template v-else-if="setCode === 'NCSC-CCC'">
      <h3>{{ infoObj.strand }}</h3>
      <div class="pre-line">{{ standardText }}</div>
    </template>

    <template v-else-if="setCode === 'NY-PreK'">
      <h3>{{ infoObj.standard_short_ref }} {{ infoObj.strand }}</h3>
      <div class="pre-line">{{ standardText }}</div>
    </template>

    <template v-else-if="setCode === 'CDC-AEM'">
      <h3>{{ infoObj.month }} Months: {{ infoObj.subject }}</h3>
      <p class="no-margin-bottom">{{ standardText }}</p>
    </template>

    <template v-else-if="setCode === 'CPLF'">
      <h3>{{ infoObj.strand }}</h3>
      <p>{{ infoObj.standard_num }} {{ infoObj.domain }}</p>
      <p class="cplf-indented" v-if="!infoObj.substandards">{{ infoObj.age }}. {{ standardText }}</p>
      <ol class="list-unstyled" v-if="infoObj.substandards">
        <li
          class="cplf-indented"
          style="margin-bottom: 12px"
          v-for="substandard of infoObj.substandards"
          :key="substandard.standard_short_ref">{{ substandard.standard_text }}</li>
      </ol>
    </template>

    <template v-else-if="setCode === 'TPKG'">
      <h3>{{ panelHeaderTextForStandard(infoObj) }}</h3>
      <div>
        <div v-html="standardText"/>
        <template v-if="infoObj.substandards">
          <ol type="a">
            <li
              v-for="substandard in infoObj.substandards"
              :key="substandard.standard_long_ref"
              v-html="substandard.standard_text"/>
          </ol>
        </template>
      </div>
    </template>

    <template v-else-if="setCode === 'TPG'">
      <h3>{{ infoObj.subject }}</h3>
      <div>
        <div v-html="standardText"/>
      </div>
    </template>

    <template v-else-if="setCode === 'CA-ELD'">
      <h3>{{ panelHeaderTextForStandard(infoObj) }}</h3>
      <p>
        <small>
          <tooltip
            trigger="hover"
            placement="top"
            :text="infoObj.part"><a>Part {{ infoObj.part_code }}</a></tooltip>
          &middot;
          <tooltip :text="infoObj.mode"><a>Mode {{ infoObj.mode_code }}</a></tooltip>
          &middot;
          <span class="text-muted">Level {{ infoObj.standard_num }}: {{ infoObj.proficiency_level }} ({{ eldStandardLevelLabel(infoObj) }})</span>
        </small>
      </p>
      <div class="pre-line">{{ standardText }}</div>
    </template>

    <template v-else-if="setCode === 'CCSS'">
      <h3>{{ infoObj.standard_short_ref }} {{ infoObj.cluster }}</h3>
      <div>
        <div v-html="standardText"/>
        <template v-if="infoObj.substandards">
          <ol type="a">
            <li
              v-for="substandard in infoObj.substandards"
              :key="substandard.standard_long_ref"
              v-html="substandard.standard_text"/>
          </ol>
        </template>
      </div>
    </template>

    <template v-else-if="setCode === 'CA-PE' || setCode === 'FL.RES' || setCode === 'WCSDC'">
      <p>{{ standardText }}</p>
    </template>

    <template v-else-if="setCode === 'ILS-SEL'">
      <h3>{{ panelHeaderTextForStandard(infoObj) }}</h3>
      <p>
        <small>
          <tooltip :text="infoObj.ils_goal"><a>Goal {{ infoObj.ils_goal_code }}</a></tooltip>
          &middot;
          <tooltip :text="infoObj.ils_standard"><a>Standard {{ infoObj.ils_standard_code }}</a></tooltip>
          &middot;
          <span class="text-muted">Grades {{ infoObj.grades_str }}</span>
          &middot;
          <span class="text-muted">Benchmark {{ infoObj.standard_num }}</span>
        </small>
      </p>
      <div class="pre-line">{{ standardText }}</div>
    </template>

    <template v-else-if="setCode === 'IELDS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard_let"
        :single-substandard-text="infoObj.substandard_text" />
      <p class="cplf-indented">{{ infoObj.footer }}</p>
    </template>

    <template v-else-if="setCode === 'TEKS'">
      <h3>{{ infoObj.strand }}</h3>
      <p>{{ infoObj.strand_description }}</p>
      <template v-if="infoObj.standard_num && infoObj.standard_num != ''">
        <div class="pre-line">{{ infoObj.standard_num }}. {{ standardText }}</div>
      </template>
    </template>

    <template v-else-if="setCode === 'ETEKS'">
      <h3>{{ infoObj.strand_domain }}</h3>
      <p>{{ infoObj.strand_domain_text }}</p>
      <p v-html="standardText"/>
    </template>

    <template v-else-if="setCode === 'TEKS.PCT'">
      <h3>{{ infoObj.strand_domain }}</h3>
      <p v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        ol-type="A"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'TEKS-2017'">
      <div class="pre-line">{{ infoObj.standard_num }}{{ infoObj.standard_letter }}{{ infoObj.standard_roman_numeral && ('.' + infoObj.standard_roman_numeral) }}. {{ standardText }}</div>
    </template>

    <template v-else-if="setCode === 'TEKS-SCI' || setCode === 'SC-CCR'">
      <h3 v-if="infoObj.hs_course">{{ infoObj.hs_course }}</h3>
      <p v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        ol-type="A"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode=== 'GSE.PE'">
      <h3 v-if="infoObj.domain">{{ infoObj.domain }}</h3>
      <p v-if="infoObj.hs_course">{{ infoObj.hs_course }}</p>
      <p v-html="standardText"/>
    </template>

    <template v-else-if="setCode === 'TEKS.CTE' || setCode === 'RIELDS' || setCode === 'TN-ELDS' || setCode === 'MT-ELS'">
      <h3>{{ infoObj.cluster }}</h3>
      <div>
        <div v-html="standardText"/>
      </div>
    </template>

    <template v-else-if="setCode === 'ITELG'">
      <h3>{{ infoObj.indicator }}</h3>
      <div>
        <div v-html="standardText"/>
      </div>
    </template>

    <template v-else-if="setCode === 'NCSCoS'">
      <h3 v-if="infoObj.math_hs_goal">{{ infoObj.math_hs_goal }}</h3>
      <h3 v-else>{{ infoObj.cluster }}</h3>

      <div v-html="generatedNCSCoSStandardText(infoObj)" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard_let"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'HELDS'">
      <h3>{{ infoObj.strand }}</h3>
      <p> {{ infoObj.topic }}</p>
      <p v-html="standardText"/>
      <p>{{ infoObj.age_group }}</p>
    </template>

    <template v-else-if="setCode === 'NCFELD'">
      <h3>{{ infoObj.cluster }}</h3>
      <p> {{ infoObj.standard_group }}</p>
      <p v-html="standardText"/>
      <p>{{ infoObj.age_range_title }}</p>
    </template>

    <template v-else-if="setCode === 'MDELS'">
      <h3>{{ infoObj.strand }}</h3>
      <p> {{ infoObj.standard_group }}</p>
      <template v-if="infoObj.standard_num && infoObj.standard_num != ''">
        <div class="pre-line">{{ infoObj.standard_num }}. {{ standardText }}</div>
      </template>
      <template v-else>
        <p v-html="standardText"/>
      </template>
    </template>

    <template v-else-if="setCode === 'HCPS'">
      <h3>{{ infoObj.strand_domain }}: {{ infoObj.standard_group }}</h3>
      <p v-html="standardText"/>
    </template>

    <template v-else-if="setCode === 'OLS'">
      <h3><template v-if="infoObj.math_hs_subject">({{ infoObj.math_hs_subject }}) </template>{{ infoObj.cluster }}</h3>

      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard_let"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'OLS-E'">
      <h3>{{ infoObj.cluster }}</h3>
      <h4>{{ infoObj.level }}</h4>
      <div v-html="standardText" />

      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard_let"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'NY.CDOS'">
      <h3>{{ infoObj.level }}</h3>
      <h4>{{ infoObj.cluster }}</h4>
      <div v-html="standardText" />

      <SubstandardBullets
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'KSECE'">
      <h3>{{ infoObj.strand }}</h3>
      <p><small><span class="text-muted">{{ infoObj.cluster }}</span></small></p>
      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'OELDS'">
      <h3>{{ infoObj.strand_domain }}: {{ infoObj.topic }}</h3>
      <div v-html="standardText" />
      <br>
      <p>{{ infoObj.age_range_title }} ({{ infoObj.age_range }})</p>
    </template>

    <template v-else-if="setCode === 'NCSC.CCC' || setCode === 'USS-ELA' || setCode === 'HEGSE' || setCode === 'ECS' || setCode === 'RISELS'">
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'MELG'">
      <h3>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <br>
      <p>{{ infoObj.age_range_title }}</p>
    </template>

    <template v-else-if="setCode === 'OLS-T'">
      <h3>Topic {{ infoObj.topic_num }}: {{ infoObj.topic }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'OLS-FL'">
      <h3>{{ infoObj.topic }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'OLS-PE'">
      <h3>{{ infoObj.umbrella_standard }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'NYSNGLS'">
      <h3>{{ (infoObj.subject === 'Math' && infoObj.level) ? `(${infoObj.level}) ${infoObj.cluster}` : infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard_let"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'MLS'">
      <h3>{{ infoObj.cluster_description }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />

      <p v-if="infoObj.priority_standard === 'TRUE'">(Missouri Priority Standard)</p>
    </template>

    <template v-else-if="setCode === 'GELDS'">
      <h3>{{ infoObj.age_range }}: {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p v-if="infoObj.kindergarten_correlation_ref_code">Kindergarten Correlation: ({{ infoObj.kindergarten_correlation_ref_code }})</p>
    </template>

    <template v-else-if="setCode === 'AELS'">
      <h3>{{ infoObj.topic_code }}: {{ infoObj.topic }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'AZ.SEL'">
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'LSS.LC' || setCode === 'NACS-PE' || setCode === 'IELG' || setCode === 'AELG' || setCode === 'AAAS'">
      <h3>{{ infoObj.strand_domain }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'LELDS'">
      <h3>{{ infoObj.strand }}</h3>
      <p v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text"
        ol-type="1" />
      <p>{{ infoObj.age_range_title }} ({{ infoObj.age_range }})</p>
    </template>

    <template v-else-if="setCode === 'VSOL'">
      <h3 v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'VASOL'">
      <h3 v-if="infoObj.strand_description"> {{ infoObj.strand_description }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'MCF'">
      <h3><span v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}</span> <span v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}</span> {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'MAELG'">
      <h3>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <br>
      <p>{{ infoObj.age_range_title }}: {{ infoObj.age_range }}</p>
    </template>

    <template v-else-if="setCode === 'PCS'">
      <template v-if="infoObj.subject_code === 'ELA'">
        <p class="no-margin-bottom">{{ infoObj.strand_domain_text }}</p>
        <h3>{{ infoObj.cluster_description }}<template v-if="infoObj.cluster_text">: {{ infoObj.cluster_text }}</template></h3>
      </template>
      <template v-if="infoObj.subject_code === 'M'">
        <h3>{{ infoObj.cluster_description }}</h3>
      </template>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'PEC' || setCode === 'PAEC'">
      <h3>{{ infoObj.cluster_description }}</h3>
      <p>{{ infoObj.cluster_text }}</p>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'SCCCR'">
      <h3><span v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}</span> {{ infoObj.cluster }}</h3>
      <p>{{ infoObj.standard_group }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'SCCCRP'">
      <h3>{{ infoObj.cluster_description }}</h3>
      <p>{{ infoObj.cluster_text }}</p>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'TSS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.math_hs_course && infoObj.math_hs_subject">{{ infoObj.math_hs_course }}: {{ infoObj.math_hs_subject }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'AKCS' || setCode === 'DLM.EE'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'INCC'">
      <h3>{{ infoObj.course }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'IELF'">
      <h3>{{ infoObj.strand }}</h3>
      <p> {{ infoObj.topic }} </p>
      <p v-html="standardText" />
      <p>{{ infoObj.age_range }}</p>
    </template>

    <template v-else-if="setCode === 'MD-ELS'">
      <h3>{{ infoObj.strand }}</h3>
      <p v-html="standardText" />
      <p>{{ infoObj.age }}</p>
    </template>

    <template v-else-if="setCode === 'ISELC'">
      <h3>{{ infoObj.standard_group }}</h3>
      <p v-html="standardText" />
      <p class="cplf-indented">({{ infoObj.level }})</p>
    </template>

    <template v-else-if="setCode === 'MCCRS'">
      <h3>{{ infoObj.cluster }} {{ infoObj.math_hs_course }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'WAAPF'">
      <h3 v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}</h3>
      <h4>{{ infoObj.standard_group }}</h4>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'WELDG'">
      <h3 >{{ infoObj.topic }}</h3>
      <p v-if="infoObj.standard_group">{{ infoObj.standard_group }}</p>
      <p v-html="standardText" />
      <p>{{ infoObj.age_range_title }} {{ infoObj.age_range }}</p>
    </template>

    <template v-else-if="setCode === 'WASEL'">
      <h3 >{{ infoObj.strand_domain }}</h3>
      <p v-html="standardText" />
      <p class="cplf-indented">{{ infoObj.age_range }}</p>
    </template>

    <template
      v-else-if="setCode === 'NVACSC' || setCode === 'OH-SEL' || setCode === 'FLB' || setCode === 'OLS.SS' || setCode === 'NGSS' || setCode === 'LSS-S'">
      <h3>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'Alternate-K-PREP'">
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'FL'">
      <h3>{{ infoObj.standard_group }} {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'MAFS' || setCode === 'LAFS' || setCode === 'SC-ELS'">
      <h3>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
    </template>

    <template v-else-if="setCode === 'MS-CCRS'">
      <h3>{{ infoObj.math_hs_course }} {{ infoObj.cluster }}</h3>
      <p v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'CAS'">
      <h3 v-if="infoObj.cluster"><template v-if="infoObj.strand_domain">{{ infoObj.strand_domain }}: </template>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'CELDG'">
      <h3>{{ infoObj.standard_group }}</h3>
      <p>{{ infoObj.age_range }}</p>
      <p v-html="standardText"/>
      <p v-if="infoObj.footer">{{ infoObj.footer }}</p>
    </template>
    <template v-else-if="setCode === 'WIDA.ELD'">
      <h3>{{ infoObj.domain }} {{ infoObj.strand }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <p v-html="standardText"/>
      <p class="text-muted">WIDA English Language Development Standards Framework, © 2020 Board of Regents of the University of Wisconsin System wida.wisc.edu</p>
    </template>
    <template v-else-if="setCode === 'ECIPs'">
      <h3>{{ infoObj.cluster }}</h3>
      <p>{{ infoObj.age_range }}: {{ standardText }}</p>
      <p v-if="infoObj.footer">{{ infoObj.footer }}: {{ infoObj.kindergarten_correlation_ref_code }}</p>
    </template>
    <template v-else-if="setCode === 'NC.ECS' || setCode === 'OELALS'">
      <h3>{{ infoObj.cluster }}</h3>

      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'MN'">
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'IDCS'">
      <h3>{{ infoObj.cluster }}</h3>

      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'IDCCC' || setCode === 'MAS'">
      <h3>{{ infoObj.cluster }}</h3>

      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'NMCCSS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.standard_group">{{ infoObj.standard_group }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'NDCS'">
      <h3><template v-if="infoObj.standard_group">{{ infoObj.standard_group }}: </template>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p
        class="cplf-indented"
        v-if="infoObj.footer"
        v-html="infoObj.footer"/>
    </template>
    <template v-else-if="setCode === 'IC'">
      <h3><template v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}: </template>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p
        class="cplf-indented"
        v-if="infoObj.footnote"
        v-html="infoObj.footnote"/>
    </template>
    <template v-else-if="setCode === 'RELDS'">
      <h3>By the following ages, most children will: {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'KELS'">
      <h3>{{ infoObj.topic }}: {{ infoObj.age_range_title }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p
        class="cplf-indented"
        v-if="infoObj.footer"
        v-html="infoObj.footer"/>
    </template>
    <template v-else-if="setCode === 'OELKS'">
      <h3>{{ infoObj.strand }}</h3>
      <div v-html="standardText" />
      <p
        class="cplf-indented"
        v-if="infoObj.age_range"
        v-html="infoObj.age_range"/>
    </template>
    <template v-else-if="setCode === 'MS-SEL'">
      <h3>{{ infoObj.standard_group }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'VELS'">
      <h3>{{ infoObj.strand }}</h3>
      <h3>{{ infoObj.substrand }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="['WCPS', 'WCSE', 'NJSLS', 'KY', 'GSE', 'GECS', 'NVACS', 'MS-AAAS', 'AZ', 'LSS', 'KS', 'IAS', 'WAS'].indexOf(setCode) > -1">
      <h3>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'NJ.SLS' || setCode === 'RICS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'WIS'">
      <h3><template v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}: </template>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard_let"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'WMELS'">
      <h3>{{ infoObj.strand }}: {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'WISEL'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="standardText"/>
      <p class="cplf-indented">{{ infoObj.age_range }}</p>
    </template>
    <template v-else-if="setCode === 'AAS'">
      <h3><template v-if="infoObj.course">{{ infoObj.course }}:</template> {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'ACS' && infoObj.subject_code === 'M'">
      <h3 v-if="infoObj.math_hs_focus_area || infoObj.math_hs_course"><template v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}<template v-if="infoObj.math_hs_focus_area">: </template></template>{{ infoObj.math_hs_focus_area }}</h3>
      <h4>{{ infoObj.cluster }}</h4>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'ACS' && infoObj.subject_code === 'ELA'">
      <h3 v-if="infoObj.cluster">{{ infoObj.cluster }}</h3>
      <p>{{ infoObj.strand }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'ALAAS' || setCode === 'NYPKELS' || setCode === 'MELDS'">
      <h3 v-if="infoObj.strand"><template v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}: </template>{{ infoObj.strand }}</h3>
      <h4 v-if="infoObj.cluster">{{ infoObj.cluster }}</h4>
      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'AL.CTE'">
      <h3>{{ infoObj.level }}</h3>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'HSA-Alt' || setCode === 'VTAA'">
      <h3>{{ infoObj.essence_statement }}</h3>
      <p v-html="standardText"/>
      <p>{{ infoObj.perf_level }}</p>
    </template>
    <template v-else-if="setCode === 'OEAF'">
      <h3 v-if="infoObj.cluster"><template v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}: </template>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <p class="cplf-indented" v-if="infoObj.level">{{ infoObj.level }}. {{ infoObj.level_text }}</p>
    </template>
    <template v-else-if="setCode === 'UTCS' || setCode == 'SDSS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}: {{ infoObj.math_hs_subject }}</p>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>

    <template v-else-if="setCode === 'WVCCRS' || setCode === 'WVAAAS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}</p>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'GMS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}</p>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'CAS-EEO'">
      <h3><template v-if="infoObj.domain">{{ infoObj.domain }}: </template>{{ infoObj.learning_expectation }}</h3>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'OKAS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="`${infoObj.math_hs_course && (infoObj.math_hs_course + ':') || ''} ${standardText}`" />
    </template>
    <template v-else-if="setCode === 'SECD'">
      <h3>{{ infoObj.topic }}</h3>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p>({{ infoObj.level }})</p>
    </template>
    <template v-else-if="setCode === 'MN-SEL'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="standardText" />
      <p v-if="infoObj.related_standard">Related Academic Standards: {{ infoObj.related_standard }}</p>
    </template>
    <template v-else-if="setCode === 'MELS'">
      <h3>{{ infoObj.topic }}</h3>
      <p v-if="infoObj.strand">{{ infoObj.strand }}</p>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p>{{ infoObj.age_range_title }}</p>
    </template>
    <template v-else-if="setCode === 'FELDS'">
      <h3>{{ infoObj.strand }}{{ infoObj.cluster && `: ${infoObj.cluster}` }}</h3>
      <p v-html="infoObj.topic" />
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'NECCRS'">
      <h3>{{ infoObj.strand }}</h3>
      <p v-if="infoObj.cluster">{{ infoObj.cluster }}</p>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'NJELS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.strand">{{ infoObj.strand }}</p>
      <p v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
      <p class="cplf-indented" v-if="infoObj.footer_header">{{ infoObj.footer_header }}: {{ infoObj.database_number }}</p>
    </template>
    <template v-else-if="setCode === 'TXELG'">
      <h3>{{ infoObj.strand }}</h3>
      <p>{{ infoObj.cluster }}: {{ standardText }}</p>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'TEKS.PE'">
      <h3>{{ infoObj.cluster }}</h3>
      <p>{{ infoObj.high_school_course }}: {{ standardText }}</p>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'TEKS.SS' || setCode === 'LA-ELDS'">
      <h3>{{ infoObj.domain }}</h3>

      <div v-html="standardText" />
      <SubstandardBullets
        ol-type="A"
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'TEKS.ELPS'">
      <h3>{{ infoObj.cluster }}</h3>

      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'OH.ELDS' || setCode === 'PTKLF'">
      <h3>{{ infoObj.strand }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'PLSEC'">
      <h3>{{ infoObj.strand }}</h3>
      <p>{{ infoObj.cluster }}: {{ infoObj.sub_cluster }}</p>
      <p v-html="standardText"/>
      <p>{{ infoObj.age }}</p>
    </template>
    <template v-else-if="setCode === 'AZELPS'">
      <h3>{{ infoObj.subject }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'MLR'">
      <h3>{{ infoObj.cluster_code }}: {{ infoObj.cluster }}</h3>
      <p>{{ infoObj.math_hs_subject }}</p>
      <p v-html="standardText"/>
      <SubstandardBullets
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'VESOL'">
      <h3>{{ infoObj.domain }} {{ infoObj.strand }}</h3>
      <p v-html="standardText"/>
      <p>{{ infoObj.complexity_continuum }}</p>
    </template>
    <template v-else-if="setCode === 'DCELS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'IELS'">
      <h3>{{ infoObj.cluster }}</h3>
      <p>{{ infoObj.standard_header }}</p>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'SC.CCR'">
      <h3>{{ infoObj.domain }}: {{ infoObj.strand }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <p v-html="standardText"/>
      <SubstandardBullets
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'NV.PKS'">
      <h3>{{ infoObj.domain }}: {{ infoObj.cluster }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'NV.ELG'">
      <h3>{{ infoObj.age_range }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'ND.CS'">
      <h3>{{ infoObj.level }}</h3>
      <p v-html="standardText" />
      <SubstandardBullets
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'NDELS' || setCode === 'ASELD' || setCode === 'NYS.SLS' || setCode === 'NDSSCS' || setCode === 'NVACS-H' || setCode === 'ELDS'">
      <h3>{{ infoObj.domain }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'ND.CTE'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-if="infoObj.strand">{{ infoObj.strand }}</p>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'INAS' || setCode === 'NCS' || setCode === 'NC.SS'">
      <h3><template v-if="infoObj.math_hs_subject">{{ infoObj.math_hs_subject }}: </template>{{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'INELS'">
      <h3>{{ infoObj.domain }}</h3>
      <p><small><span class="text-muted">{{ infoObj.strand }}</span></small></p>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'NYSS'">
      <h3>{{ infoObj.strand }}</h3>
      <p>{{ infoObj.domain }}</p>
      <p v-html="standardText" />
    </template>
    <template
      v-else-if="setCode === 'CTELDS' || setCode === 'ME-ELDS' || setCode === 'NDPECS' || setCode === 'NVESCRS'
        || setCode === 'GSELA' || setCode === 'IDCS.ELAL' || setCode === 'TS' || setCode === 'ELG' || setCode === 'NECCRSEI'
        || setCode === 'IAS-PE' || setCode === 'MD-PE' || setCode === 'NE-SS'">
      <h3>{{ infoObj.strand }}</h3>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'AR'">
      <h3><template v-if="infoObj.strand">{{ infoObj.strand }}:</template>{{ infoObj.cluster }}</h3>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'OLS.S'">
      <h3>{{ infoObj.domain }}</h3>
      <p>{{ infoObj.cluster }}</p>
      <p v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'NDES'">
      <h3>{{ infoObj.cluster }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'MLS.S'">
      <h3>{{ infoObj.domain }}</h3>
      <p>{{ infoObj.strand }}</p>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'TEKS-SS'">
      <h3><span v-if="infoObj.high_school_course">{{ infoObj.high_school_course }}:</span> {{ infoObj.domain }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text"
        ol-type="A"/>
    </template>
    <template v-else-if="setCode === 'NHES'">
      <h3>{{ infoObj.subject }}: {{ infoObj.domain }}</h3>
      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'SOL'">
      <h3 v-if="infoObj.math_hs_course">{{ infoObj.math_hs_course }}</h3>
      <h3 v-else-if="infoObj.strand">{{ infoObj.strand }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text"
        ol-type="i"/>
    </template>
    <template v-else-if="setCode === 'TEKS.SCI'">
      <h3>{{ infoObj.strand }}</h3>
      <p v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text"
        ol-type="A" />
    </template>
    <template v-else-if="setCode === 'NPKS' || setCode === 'NH-ELS'">
      <h4>{{ infoObj.cluster }}</h4>
      <div v-html="standardText" />
    </template>
    <template v-else-if="setCode === 'KSELA' || setCode === 'TEKS-P' || setCode === 'SC' || setCode === 'NE-PE'">
      <h3>{{ infoObj.strand }}</h3>
      <p v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else-if="setCode === 'CA-T-SEL'">
      <h3>{{ infoObj.age_range }}: {{ infoObj.strand }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'NDSCS'">
      <h3>{{ infoObj.descriptor }}</h3>
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'ELP'">
      <p v-html="standardText"/>
    </template>
    <template v-else-if="setCode === 'HICCS'">
      <h3>{{ infoObj.strand_domain }}: {{ infoObj.cluster }}</h3>
      <div v-html="standardText" />
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
    <template v-else>
      <h3>{{ shortRef }}</h3>
      <div v-html="standardText"/>
      <SubstandardBullets
        :ordered-substandards="infoObj.substandards"
        :single-substandard-bullet="infoObj.substandard"
        :single-substandard-text="infoObj.substandard_text" />
    </template>
  </div>
</template>

<script>
import {
  panelHeaderTextForStandard,
  eldStandardLevelLabel
} from '@/components/goals/common/standard_panel_header_text.js'
import SubstandardBullets from './SubstandardBullets'

export default {
  name: 'StandardView',
  components: { SubstandardBullets },
  props: {
    setCode: {
      type: String,
      required: true
    },
    longRef: {
      type: String,
      required: true
    },
    shortRef: {
      type: String,
      required: true
    },
    standardText: {
      type: String,
      required: true
    },
    corePurpose: {
      type: String,
      default: ''
    },
    infoObj: {
      type: Object,
      default: () => {}
    },
    editLink: {
      type: String,
      default: ''
    }
  },
  methods: {
    panelHeaderTextForStandard,
    eldStandardLevelLabel(standard) {
      return eldStandardLevelLabel(standard)
    },
    generatedNCSCoSStandardText(standard) {
      let prefixText = ''
      if (standard.math_hs_goal) {
        prefixText = `(${standard.math_hs_course})`
      } else if (standard.level) {
        prefixText = `(${standard.level})`
      }
      return `${prefixText} ${this.standardText}`
    },
    listItemIndexForLowercaseLetter(letter) {
      let startCharCode = 'a'.charCodeAt(0)
      let letterCharCode = letter.charCodeAt(0)
      return letterCharCode - startCharCode + 1
    }
  }
}
</script>

<style lang="less" scoped>
blockquote {
  font-size: 12px;
  padding: 4px 8px;
  margin: 4px 0;
}
@media print {
  blockquote {
    border: 0;
  }
}

h3,
h4 {
  font-size: 14px;
  line-height: 1.4em;
  font-weight: bold;
  margin-top: 10px;
}
h4 {
  margin-bottom: 2px;
}

.cplf-indented {
  margin-top: 12px;
  padding-left: 16px;
  line-height: 1.125em;
  font-size: 85%;
}
/deep/ .indent {
  display: block;
  padding: 0.5em 1em;
}
</style>
